import classNames from 'classnames';
import React, { useMemo } from 'react';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';
import { useViewType, ViewType } from '@eika/hooks';
import { IconTypes } from '@eika/icon';

import EFakturaList from '../../components/EFakturaList/EFakturaList';
import { useConfig } from '../../defaultConfig';
import useDocumentsWaitingForSigning from '../../fetchHooks/useDocumentsWaitingForSigning';
import useEfaktura, { notAvtalegiroEfakturas } from '../../fetchHooks/useEfaktura';
import { useKredittbankenTasks } from '../../fetchHooks/useKredittbankenTasks';
import { useLoanApplications } from '../../fetchHooks/useLoans';
import { useOutboxQuery } from '../../fetchHooks/useOutbox';
import { noStoppedCrossBorderPayments, useStoppedPaymentItems } from '../../fetchHooks/usePaymentItems';
import useTranslation from '../../hooks/useTranslation';
import GeneralSystemErrorCard from './GeneralSystemErrorCard';
import NoTasksCard from './NoTasksCard';
import TaskManagerListItem from './TaskManagerListItem';
import type { TaskManagerType } from './TaskManagerTypes';
import { getTotalLoanApplicationCount, mapLoanApplications } from './loanApplicationMapper';

import './TaskManager.scss';

const TaskManager = () => {
    const translated = useTranslation();
    const {
        featureToggles,
        ahvSigningLink,
        stoppedPaymentsLink,
        paymentsForApprovalLink,
        approvalListLink,
        loanApplicationLink,
        kredittbankenKycLink,
    } = useConfig();

    const {
        data: approvalOutbox = [],
        error: outboxError,
        isLoading: outboxLoading,
    } = useOutboxQuery(featureToggles.enableBulkPayment);
    const isOutboxError = !!outboxError;

    const {
        data: numberOfDocuments = 0,
        error: documentsError,
        isLoading: documentsLoading,
    } = useDocumentsWaitingForSigning(featureToggles.enableGettingDocumentsForSigning);
    const isDocumentsError = !!documentsError;

    const {
        data: kycTasks = [],
        error: kredittbankenTasksError,
        isLoading: kredittbankenTasksLoading,
    } = useKredittbankenTasks({
        enabled: featureToggles.enableKredittbankenTasks,
        select: (tasks) => tasks.filter((task) => task.category === 'ANSWER_KYC'),
    });
    const isKredittbankenTasksError = !!kredittbankenTasksError;

    const {
        data: stoppedPayments = [],
        isLoading: stoppedPaymentsLoading,
        error: stoppedPaymentsError,
    } = useStoppedPaymentItems(featureToggles.enableRejectedPaymentItems, {
        select: !featureToggles.enableStoppedCrossBorderPayments ? noStoppedCrossBorderPayments : undefined,
    });
    const isStoppedPaymentsError = !!stoppedPaymentsError;

    const {
        data: eFakturas = [],
        isLoading: eFakturasLoading,
        ...efakturaState
    } = useEfaktura({ select: notAvtalegiroEfakturas, enabled: !featureToggles.disableEfaktura });
    const isEfakturaError = !!efakturaState.error;

    const serviceErrors: Partial<Record<TaskManagerType, RequestExceptionWithResponse | null>> = {
        outbox: outboxError,
        documents: documentsError,
        stopped: stoppedPaymentsError,
        eFaktura: efakturaState.error,
    };

    const { data: loanApplications = [], isLoading: loanApplicationsLoading } = useLoanApplications(
        featureToggles.enableLoanApplication,
    );

    const viewType = useViewType();

    const isMobile = viewType !== ViewType.desktop;

    const totalLoanApplicationCount = getTotalLoanApplicationCount(loanApplications);
    const mappedLoanApplications = useMemo(
        () => mapLoanApplications(translated, loanApplicationLink)(loanApplications),
        [translated, loanApplications, loanApplicationLink],
    );

    const combinedCount =
        approvalOutbox.length +
        numberOfDocuments +
        stoppedPayments.length +
        eFakturas.length +
        totalLoanApplicationCount +
        kycTasks.length;

    const isLoading =
        outboxLoading ||
        documentsLoading ||
        stoppedPaymentsLoading ||
        loanApplicationsLoading ||
        kredittbankenTasksLoading;
    const isError =
        isOutboxError || isDocumentsError || isStoppedPaymentsError || isEfakturaError || isKredittbankenTasksError;

    const isLoadingOtherThingsAndEfakturaIsEmpty =
        !eFakturasLoading && eFakturas.length === 0 && isLoading && combinedCount === 0;

    const hideInSigningRoom = featureToggles.enableHideSigningRoomLinkInSigningRoom;

    const isEmpty = combinedCount === 0 && !isLoading && !isError;
    if (isEmpty && isMobile) return null;

    return (
        <section className="task-manager__wrapper sgw-content">
            <h2 className="typography-subtitle-bold task-manager__title">
                {hideInSigningRoom ? translated.otherTasks : translated.tasks}
            </h2>
            <div
                className={classNames('task-manager__list-wrapper', {
                    empty: isEmpty,
                })}
            >
                <GeneralSystemErrorCard errors={serviceErrors} />
                {isEmpty ? (
                    <NoTasksCard />
                ) : (
                    <>
                        <TaskManagerListItem
                            link={paymentsForApprovalLink}
                            title={translated.paymentsForApproval}
                            count={approvalOutbox.length}
                            type="outbox"
                            isLoading={stoppedPaymentsLoading || outboxLoading}
                        />
                        <TaskManagerListItem
                            link={stoppedPaymentsLink}
                            title={translated.stoppedPayments.title}
                            count={stoppedPayments.length}
                            type="stopped"
                        />
                        {!hideInSigningRoom && (
                            <TaskManagerListItem
                                link={ahvSigningLink}
                                title={translated.documentsForSigning}
                                count={numberOfDocuments}
                                type="documents"
                            />
                        )}
                        {kycTasks.map((task) => (
                            <TaskManagerListItem
                                key={task.category}
                                link={kredittbankenKycLink}
                                title={translated.kredittbanken.category[task.category]}
                                count={kycTasks.length}
                                type="kredittbanken"
                                icon={IconTypes.KLOKKE_24}
                            />
                        ))}
                        {mappedLoanApplications.map((application) => (
                            <TaskManagerListItem
                                key={application.id.value}
                                link={application.link}
                                type="loan-application"
                                title={translated.loanApplications.type[application.type]}
                                count={application.numberOfTasks}
                                labels={application.labels}
                                attentionColor={application.attentionColor}
                                showWithEmptyCount
                            />
                        ))}
                        {eFakturas.length === 1 && !eFakturasLoading && !isEfakturaError ? (
                            <EFakturaList
                                eFakturas={eFakturas}
                                type="task-manager-list"
                                error={null}
                                isLoading={eFakturasLoading || isLoadingOtherThingsAndEfakturaIsEmpty}
                            />
                        ) : (
                            <TaskManagerListItem
                                link={approvalListLink}
                                title={translated.eFaktura.eFaktura}
                                count={eFakturas.length}
                                type="eFaktura"
                                isLoading={eFakturasLoading || isLoadingOtherThingsAndEfakturaIsEmpty}
                            >
                                <EFakturaList
                                    eFakturas={eFakturas}
                                    error={null}
                                    isLoading={eFakturasLoading || isLoadingOtherThingsAndEfakturaIsEmpty}
                                    type="task-manager-list"
                                />
                            </TaskManagerListItem>
                        )}
                    </>
                )}
            </div>
        </section>
    );
};

export default TaskManager;
